"use client";

import {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
    type ReactNode,
} from "react";
import { createClient } from "@/lib/supabase/client";
import type { User } from "@supabase/supabase-js";
import {
    PROFILE_NAME_CHANGED_EVENT,
    type ProfileNameChangedDetail,
} from "@/lib/profile/client-events";
import {
    createAuthIdentityFingerprint,
    readAuthDocumentGeneration,
    reconcileAuthDocumentIdentity,
    reloadStaleAuthDocument,
    showAuthTransitionCurtain,
    subscribeToAuthDocumentTransitions,
} from "@/lib/auth/document-transition";

// ─── Types ───────────────────────────────────────────────

export interface AuthProfile {
    displayName: string;
    avatarUrl: string | null;
    /**
     * Author-access = author capability OR admin. Personalization only (e.g.
     * showing the "Write" navbar pill). Access control stays server-side in
     * middleware — never gate anything security-relevant on this client value.
     */
    canAuthor: boolean;
}

interface AuthContextValue {
    user: User | null;
    profile: AuthProfile | null;
    loading: boolean;
}

// ─── Context ─────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue>({
    user: null,
    profile: null,
    loading: true,
});

export function useAuth() {
    return useContext(AuthContext);
}

// ─── Provider ────────────────────────────────────────────

interface AuthProviderProps {
    children: ReactNode;
}

// Auth is bootstrapped ENTIRELY client-side: the root layout deliberately
// reads no cookies/session (a server read there would force every route
// dynamic and void all ISR caching — the July 2026 Vercel bill failure mode).
// Every page therefore behaves like the old `force-static` routes always did:
// SSR HTML renders the anonymous state, and onAuthStateChange below recovers
// the stored session immediately after hydration.
export function AuthProvider({ children }: AuthProviderProps) {
    const [user, setUser] = useState<User | null>(null);
    const [profile, setProfile] = useState<AuthProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const activeUserIdRef = useRef<string | null>(null);
    const profileFetchGenerationRef = useRef(0);
    const displayNameOverrideRef = useRef<ProfileNameChangedDetail | null>(null);
    const mountedAuthGenerationRef = useRef<string | null>(null);
    const activeAuthFingerprintRef = useRef<string | null>(null);
    const authFingerprintRequestRef = useRef(0);
    const hasObservedAuthEventRef = useRef(false);

    useEffect(() => {
        mountedAuthGenerationRef.current = readAuthDocumentGeneration();
        const reloadIfStale = () => {
            reloadStaleAuthDocument(mountedAuthGenerationRef.current);
        };

        // Chromium can restore an older document while pageshow.persisted is
        // false. Focus catches a suspended tab that missed live sync delivery.
        window.addEventListener("pageshow", reloadIfStale);
        window.addEventListener("focus", reloadIfStale);
        const unsubscribeTransitions = subscribeToAuthDocumentTransitions(
            () => mountedAuthGenerationRef.current,
        );
        return () => {
            window.removeEventListener("pageshow", reloadIfStale);
            window.removeEventListener("focus", reloadIfStale);
            unsubscribeTransitions();
        };
    }, []);

    useEffect(() => {
        const supabase = createClient();

        // onAuthStateChange is the single source of truth for client-side auth.
        // It fires INITIAL_SESSION once on mount (with the stored session, or
        // null) and again on every sign-in / sign-out / token refresh — so it
        // covers the "SSR had no user" case (force-static pages) without a
        // separate getUser() round-trip, and reliably resolves `loading`.
        // Note: this uses the session from storage, not a server-validated
        // getUser(). That's fine here — `role`/avatar are personalization only;
        // real access control lives in middleware (see AuthProfile above).
        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((_event, session) => {
            // Supabase normally labels the first callback INITIAL_SESSION, but
            // an SSR-cookie session can surface as SIGNED_IN on a fresh browser
            // client. The first observed callback always bootstraps this new
            // document; treating its label as a persistent-document account
            // switch causes an infinite reload loop.
            const isDocumentBootstrap = !hasObservedAuthEventRef.current;
            hasObservedAuthEventRef.current = true;
            const newUser = session?.user ?? null;
            const newUserId = newUser?.id ?? null;
            const hasUserChanged = activeUserIdRef.current !== newUserId;

            // Browser-client identity changes can arrive without a Server
            // Action caller (notably recovery hashes). Hide account A before
            // asynchronous reconciliation can expose a mixed A/B document.
            // INITIAL_SESSION always belongs to a fresh document.
            if (!isDocumentBootstrap && hasUserChanged) {
                showAuthTransitionCurtain();
            }

            activeUserIdRef.current = newUserId;
            if (
                displayNameOverrideRef.current &&
                displayNameOverrideRef.current.userId !== newUserId
            ) {
                displayNameOverrideRef.current = null;
            }
            const fetchGeneration = ++profileFetchGenerationRef.current;
            const fingerprintRequest = ++authFingerprintRequestRef.current;
            setUser(newUser);
            // Auth is now determined — clear loading immediately, independent of
            // the profile fetch below. The avatar can render from `user` while
            // the profile (display name / avatar) fills in a moment later.
            setLoading(false);

            void createAuthIdentityFingerprint(
                newUser
                    ? { id: newUser.id, email: newUser.email ?? null }
                    : null,
            ).then((fingerprint) => {
                if (
                    authFingerprintRequestRef.current !== fingerprintRequest ||
                    activeUserIdRef.current !== newUserId
                ) {
                    return;
                }

                // A different tab may have advanced the shared epoch while
                // this document was hashing its bootstrap identity, before the
                // queued storage event reached us. Reload first so account A
                // cannot bind itself to account B's unresolved transition and
                // then ignore that same-generation event.
                if (
                    reloadStaleAuthDocument(
                        mountedAuthGenerationRef.current,
                    )
                ) {
                    return;
                }

                const previousFingerprint = activeAuthFingerprintRef.current;
                const expectedMountedGeneration =
                    mountedAuthGenerationRef.current;
                const reconciliation = reconcileAuthDocumentIdentity(
                    fingerprint,
                    undefined,
                    expectedMountedGeneration,
                );
                if (reconciliation.stale) {
                    reloadStaleAuthDocument(expectedMountedGeneration);
                    return;
                }
                mountedAuthGenerationRef.current = reconciliation.generation;
                activeAuthFingerprintRef.current = fingerprint;

                // A non-initial identity/email change occurred in a persistent
                // document. Reload this source tab as well as peers so every
                // account-scoped React/Zustand consumer is discarded.
                if (
                    !isDocumentBootstrap &&
                    (hasUserChanged ||
                        (previousFingerprint !== null &&
                            previousFingerprint !== fingerprint))
                ) {
                    showAuthTransitionCurtain();
                    window.location.reload();
                }
            });

            if (!newUser) {
                setProfile(null);
                return;
            }

            // Never show the prior account's profile while a newly signed-in
            // user's deferred profile request is in flight.
            if (hasUserChanged) setProfile(null);

            // Fetch the profile OUTSIDE this callback. Awaiting a supabase query
            // synchronously inside onAuthStateChange deadlocks on the client's
            // internal auth lock (the callback holds it; the query waits on it),
            // so the request silently never fires — which left `profile` null on
            // pages that have no server-provided initialProfile (force-static
            // routes like /writers). A 0ms defer lets the callback return and
            // release the lock first. See supabase/auth-js lock-contention issue.
            setTimeout(async () => {
                // canAuthor = author capability OR admin. Admin is read from the
                // user's own user_roles row (RLS allows selecting own rows), so a
                // pure admin still gets the author door on force-static pages
                // where the server didn't supply initialProfile.
                const [{ data }, { data: adminRow }] = await Promise.all([
                    supabase
                        .from("profiles")
                        .select("display_name, avatar_url, is_author")
                        .eq("id", newUser.id)
                        .single(),
                    supabase
                        .from("user_roles")
                        .select("user_id")
                        .eq("user_id", newUser.id)
                        .eq("role", "admin")
                        .maybeSingle(),
                ]);

                // Auth events can arrive faster than these deferred requests
                // resolve (for example, signing into a second account in an
                // already-authenticated browser). Ignore stale results so the
                // previous user's profile cannot overwrite the active one.
                if (profileFetchGenerationRef.current !== fetchGeneration) return;
                const displayNameOverride =
                    displayNameOverrideRef.current?.userId === newUser.id
                        ? displayNameOverrideRef.current.displayName
                        : null;
                if (displayNameOverride !== null) {
                    displayNameOverrideRef.current = null;
                }
                setProfile(
                    data
                        ? {
                              displayName: displayNameOverride ?? data.display_name ?? "",
                              avatarUrl: data.avatar_url,
                              canAuthor: (data.is_author ?? false) || !!adminRow,
                          }
                        : null
                );
            }, 0);
        });

        return () => {
            profileFetchGenerationRef.current += 1;
            authFingerprintRequestRef.current += 1;
            subscription.unsubscribe();
        };
    }, []);

    useEffect(() => {
        function handleProfileNameChanged(event: Event) {
            const detail = (event as CustomEvent<Partial<ProfileNameChangedDetail>>).detail;
            if (
                typeof detail?.userId !== "string" ||
                typeof detail.displayName !== "string" ||
                activeUserIdRef.current !== detail.userId
            ) {
                return;
            }

            // Retain the saved name while an initial profile request is in
            // flight, then apply it to that response rather than discarding the
            // only request that can populate avatar/capability information.
            displayNameOverrideRef.current = {
                userId: detail.userId,
                displayName: detail.displayName,
            };
            setProfile((current) =>
                current ? { ...current, displayName: detail.displayName as string } : current,
            );
        }

        window.addEventListener(PROFILE_NAME_CHANGED_EVENT, handleProfileNameChanged);
        return () =>
            window.removeEventListener(
                PROFILE_NAME_CHANGED_EVENT,
                handleProfileNameChanged,
            );
    }, []);

    return (
        <AuthContext.Provider value={{ user, profile, loading }}>
            {children}
        </AuthContext.Provider>
    );
}
